// src/oura/merchant/Documents.tsx
//
// "Your documents" on Decisions, and one document's sheet in the merchant
// drawer.
//
// The card lists every Google Doc shared with the merchant, in groups by who
// moves next: Needs your reply (open), then Being revised, Answered and
// Nothing to answer (folded). A row is 1 line: the file's icon, the title,
// and for a doc that needs them its date ("Reply by Wed 30 Sep", or "Was due
// Thu 24 Sep" in rose). The group head says the state; the row opens the
// drawer, and from 40rem wide an Open icon goes straight to the document.
//
// Inside To do's Documents tab (flat) a row opens nothing: everything the
// drawer held is on the row (DocListRow), its category, when it was shared
// and updated, what it needs from them, and the link to the file itself. The
// drawer stays for the queue (To answer, Home's Needs you, ?doc= links).
//
// The sheet (DocSheet) says what the doc asks of them now: the questions
// waiting on them, numbered, whole. Phase 1: they answer in the document, as
// comments, and Oura reads them, so nothing here answers, resolves or edits.
// The one action is opening the doc. In the queue, opening it offers the
// next item: the doc itself stays in the queue until its replies are read.
// Phase 2, behind the Docs write switch (DOCS_WRITE_ON): they answer here,
// one question at a time (drawers/DocAnswer.tsx), and Oura posts each answer
// into the doc's thread for them. "You answered" lists what they said and
// when, on any doc whose read carries it.
//
// Merchant words only (docAsks.ts): no codes, no staff states.

import { useEffect, useState, type ReactNode } from "react";
import { clientArea } from "../merchantV2";
import { ChevronRightIcon, ExternalLinkIcon, FileTextIcon } from "lucide-react";
import type { MerchantDocument } from "@service/aeo/clientDocs";
import { markDocumentRead, type ClientPagePayload } from "@service/aeo/clientReview";
import { readViewAs } from "@util/viewAs";
import {
    ArrowLink,
    CardEmpty,
    DrawerBlock,
    DrawerFoot,
    DrawerHead,
    DrawerNotice,
    FOCUS_RING,
    FoldCard,
    GroupFold,
    INSET_HAIRLINE,
    IconSquare,
    LATE_TEXT,
    MERCHANT_DOC_STATUS,
    PillButton,
    PillLink,
    ROW_HOVER,
    RowOpenLink,
    SURFACE,
    ShowMore,
    TYPE,
    Tag,
    type DrawerStatus,
} from "@oura/ui";
import { MERCHANT_OPEN_LABEL, docDate, docDateYear } from "@oura/docs";
import { cn } from "../lib/cn";
import { accountManager, type AnswerDraft } from "./asks";
import { DOCS_WRITE_ON } from "../docs/writeFlag";
import { AnsweredFold, DocAnswer } from "./drawers/DocAnswer";
import {
    DOC_GROUP_ORDER,
    askedText,
    docCategory,
    docDue,
    docPurpose,
    docTitle,
    docWaitingCount,
    fileTypeOf,
    isDocFresh,
    groupDocs,
    isDocLate,
    purposeList,
    questionText,
} from "./docAsks";
import type { SheetFlow } from "./drawers/sheetFlow";

/** Rows shown in a group that needs nothing from them, before "Show N more". */
const SHOWN = 3;

/** The title: 2 lines in a narrow row, 1 cut line from 40rem. Literal, so the JIT sees it. */
const TITLE_CUT = "line-clamp-2 break-words [@container(min-width:40rem)]:line-clamp-none [@container(min-width:40rem)]:truncate";

/** The name a screen reader hears for the Open icon and the foot's link. */
const openName = (doc: MerchantDocument) => `${MERCHANT_OPEN_LABEL}: ${docTitle(doc)}. Opens in a new tab.`;

/**
 * One doc: the file's icon, its title, the date that matters while it needs
 * them, the Open icon and a chevron. The title's button stretches over the
 * row, so the row opens from anywhere; the Open icon sits above it. The row
 * reads its own width: under 22rem the date drops under the title.
 */
export function MerchantDocRow({ doc, onOpen }: { doc: MerchantDocument; onOpen: (id: string) => void }) {
    const type = fileTypeOf(doc);
    const title = docTitle(doc);
    // The group head says the state on screen; a screen reader hears it with the row.
    const status = MERCHANT_DOC_STATUS[doc.state] ?? MERCHANT_DOC_STATUS.nothing_asked;
    const due = docDue(doc);
    const dueTone = due?.late ? LATE_TEXT : "text-gray-500";
    // Phase 2: the count up front, "2 questions waiting · Reply by Wed 7 Oct". Phase 1 keeps the date alone.
    const waiting = DOCS_WRITE_ON && due ? doc.openQuestions.filter((q) => questionText(q.text)).length : 0;
    const dueText = due ? (waiting > 0 ? `${waiting} ${waiting === 1 ? "question" : "questions"} waiting · ${due.text}` : due.text) : null;
    return (
        // INSET_HAIRLINE makes the row `relative`, so the title button's click
        // area (its ::after) covers this row only; the Open link sits above it.
        <div className={cn("flex min-h-12 w-full items-center gap-3 py-2 [container-type:inline-size]", INSET_HAIRLINE("square"), ROW_HOVER)}>
            <IconSquare tone={type.tone} icon={type.icon} />
            <div className="min-w-0 flex-1">
                <button
                    type="button"
                    data-doc-id={doc.id}
                    onClick={() => onOpen(doc.id)}
                    title={title}
                    className={cn(
                        TYPE.tb,
                        "block w-full min-w-0 text-left text-gray-900 group-hover:text-blue-600",
                        "after:absolute after:inset-0 after:rounded-lg after:content-['']",
                        "focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-blue-600",
                    )}
                >
                    <span className={TITLE_CUT}>{title}</span>
                    <span className="sr-only">, {status.label}</span>
                </button>
                {due && <span className={cn(TYPE.tc, "block tabular-nums [@container(min-width:22rem)]:hidden", dueTone)}>{dueText}</span>}
            </div>
            {due && <span className={cn(TYPE.tc, "hidden shrink-0 whitespace-nowrap tabular-nums [@container(min-width:22rem)]:inline", dueTone)}>{dueText}</span>}
            {doc.url ? (
                <RowOpenLink href={doc.url} label={openName(doc)} tip={MERCHANT_OPEN_LABEL} align="center" />
            ) : (
                // Its space, so the dates still line up with the rows that have one.
                <span aria-hidden className="hidden w-7 shrink-0 [@container(min-width:40rem)]:block" />
            )}
            <ChevronRightIcon aria-hidden size={14} className="shrink-0 text-gray-400" />
        </div>
    );
}

/**
 * One doc in the Documents tab, whole on the row, with no drawer behind it:
 * the file's icon, its title, its category and the dates on one line (and, while it needs
 * them, how many questions wait and by when), and "View" to the file on the
 * right, the same text link as Reports' and Visibility's. Under 640px it drops
 * under the text. No file type: every shared file is a Google Doc for now.
 */
export function DocListRow({
    doc,
    payload,
    fresh = false,
    onView,
    title: shownTitle,
}: {
    doc: MerchantDocument;
    payload: ClientPagePayload;
    /** The name to show; absent, the doc's own title (`docTitle`). The Shared docs list passes its file name (#4731). */
    title?: string;
    /** Not opened yet (ourteam-backend#6108): a blue dot before the title, as on Reports. */
    fresh?: boolean;
    /** View was clicked: the tab marks it read. */
    onView?: () => void;
}) {
    const type = fileTypeOf(doc);
    const title = shownTitle ?? docTitle(doc);
    const status = MERCHANT_DOC_STATUS[doc.state] ?? MERCHANT_DOC_STATUS.nothing_asked;
    const needs = doc.state === "needs_your_reply";
    const category = docCategory(doc);
    const shared = docDateYear(doc.sharedAt);
    const updated = docDateYear(doc.updatedAt);
    const due = needs ? docDue(doc) : null;
    const waiting = needs ? docWaitingCount(doc) : 0;
    const answered = doc.questions?.approved ?? 0;
    // The category as a chip (#4740), then when it came: "Shared on Thu 15 Oct 2026 • Updated ...".
    // As plain text at the head of the line it read as one of the dates.
    const meta = [shared && `Shared on ${shared}`, updated && updated !== shared && `Updated ${updated}`, answered > 0 && `${answered} answered`]
        .filter(Boolean)
        .join(" • ");
    const asks = [waiting > 0 && `${waiting} ${waiting === 1 ? "question" : "questions"} waiting`, due?.text].filter(Boolean).join(" · ");
    return (
        // The whole row opens the doc (22 Oct 2026), with the row hover; a row with no file has nothing to open.
        <div className={cn("flex flex-col gap-3 py-3.5 sm:flex-row sm:items-center sm:gap-4", INSET_HAIRLINE("square"), doc.url && ROW_HOVER)}>
            <div className="flex min-w-0 flex-1 items-start gap-3">
                <IconSquare tone={type.tone} icon={type.icon} />
                <div className="min-w-0 flex-1">
                    <p className={cn(TYPE.tb, "m-0 break-words font-medium text-gray-900")}>
                        {fresh && <span aria-hidden className="mr-2 inline-block h-2 w-2 rounded-full bg-blue-600 align-middle" />}
                        {title}
                        {fresh && <span className="sr-only"> (not opened yet)</span>}
                        <span className="sr-only">, {status.label}</span>
                    </p>
                    {asks && <p className={cn(TYPE.tc, "m-0 mt-0.5 tabular-nums", due?.late ? LATE_TEXT : "font-medium text-amber-700")}>{asks}</p>}
                    {doc.state === "with_us" && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{accountManager(payload)} replies next, in the document.</p>}
                    {(category || meta) && (
                        <p className={cn(TYPE.tc, "m-0 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 tabular-nums text-gray-500")}>
                            {category && <Tag tone="gray">{category}</Tag>}
                            {meta && <span>{meta}</span>}
                        </p>
                    )}
                </div>
            </div>
            {doc.url ? (
                <ArrowLink stretch href={doc.url} newTab onClick={onView} ariaLabel={`View ${title} in Google Docs. Opens in a new tab.`} className="ml-11 shrink-0 self-start sm:ml-0 sm:self-center">
                    View
                </ArrowLink>
            ) : (
                <span className={cn(TYPE.tc, "ml-11 shrink-0 text-gray-500 sm:ml-0")}>It&rsquo;s in your email</span>
            )}
        </div>
    );
}

/**
 * The card. Nothing at all while Oura does not read docs for this business
 * (`documents` absent); one calm line while nothing is shared with them.
 */
export function DocumentsCard({
    documents,
    refreshFailed,
    onOpen,
    flat,
    payload,
    readNow,
    onView,
}: {
    documents: MerchantDocument[] | undefined;
    /** The last read again failed: the rows on screen are the last good list. */
    refreshFailed: boolean;
    onOpen: (id: string) => void;
    /**
     * Inside a tab, which is already the card: no fold, no card chrome, and
     * the groups open (#4062 MR-FE-6). A tab that opens onto four more folds
     * is the thing the tabs were meant to remove.
     */
    flat?: boolean;
    /** For what each doc is for and who replies next, on the flat rows. */
    payload?: ClientPagePayload;
    /** Docs opened on this visit, before the next read of the page says so. */
    readNow?: ReadonlySet<string>;
    /** View was clicked on a flat row. */
    onView?: (id: string) => void;
}) {
    if (!documents) return null;
    const groups = groupDocs(documents);
    const needing = groups.needs_your_reply.length;
    const late = groups.needs_your_reply.filter(isDocLate).length;
    const lateNote = late > 0 ? <span className={LATE_TEXT}>{late} late</span> : null;
    const row = (doc: MerchantDocument) => <MerchantDocRow key={doc.id} doc={doc} onOpen={onOpen} />;
    const body = (
        <>
            {refreshFailed && (
                <p role="status" className={cn(TYPE.tc, "m-0 mb-3 rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200")}>
                    Could not refresh your documents. It will retry in a minute.
                </p>
            )}
            {documents.length === 0 ? (
                <CardEmpty icon={FileTextIcon} title="Nothing shared with you yet">We will let you know when a document is ready for you.</CardEmpty>
            ) : (
                <div className="space-y-4">
                    {DOC_GROUP_ORDER.filter((key) => groups[key].length > 0).map((key) => (
                        <section key={key}>
                            <h3 className={cn(TYPE.lab, "m-0 mb-1")}>
                                {MERCHANT_DOC_STATUS[key].label} <span className="tabular-nums text-gray-400">{groups[key].length}</span>
                            </h3>
                            <div>{groups[key].map((doc) =>
                                    payload ? (
                                        <DocListRow key={doc.id} doc={doc} payload={payload} fresh={isDocFresh(doc, readNow)} onView={() => onView?.(doc.id)} />
                                    ) : (
                                        row(doc)
                                    ),
                                )}</div>
                        </section>
                    ))}
                </div>
            )}
        </>
    );
    if (flat) return body;
    return (
        <FoldCard
            id="documents"
            anchor="documents"
            title="Your documents"
            icon={{ icon: FileTextIcon, area: clientArea("setup") }}
            foldedFact={
                documents.length === 0 ? (
                    "Nothing shared yet"
                ) : needing > 0 ? (
                    <span className="tabular-nums">
                        {needing} {needing === 1 ? "needs" : "need"} your reply
                        {lateNote && <> · {lateNote}</>}
                    </span>
                ) : (
                    "0 pending"
                )
            }
        >
            {refreshFailed && (
                <p role="status" className={cn(TYPE.tc, "m-0 mb-3 rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200")}>
                    Could not refresh your documents. It will retry in a minute.
                </p>
            )}
            {documents.length === 0 ? (
                <CardEmpty icon={FileTextIcon} title="Nothing shared with you yet">We will let you know when a document is ready for you.</CardEmpty>
            ) : (
                // The groups are the only children of this box, so the first loses its divider.
                <div>
                    {DOC_GROUP_ORDER.filter((key) => groups[key].length > 0).map((key) => {
                        const status = MERCHANT_DOC_STATUS[key];
                        const list = groups[key];
                        const needs = key === "needs_your_reply";
                        // Everything that needs them shows; a group that needs nothing shows a few.
                        const shown = needs ? list : list.slice(0, SHOWN);
                        return (
                            <GroupFold
                                key={key}
                                id={`documents.${key}`}
                                lead="square"
                                rowEnd="open"
                                tone={status.tone}
                                title={status.label}
                                count={list.length}
                                defaultFolded={!needs}
                                note={needs ? lateNote : undefined}
                            >
                                <div>
                                    {shown.map(row)}
                                    <ShowMore count={list.length - shown.length}>{list.slice(shown.length).map(row)}</ShowMore>
                                </div>
                            </GroupFold>
                        );
                    })}
                </div>
            )}
        </FoldCard>
    );
}

/** A comment's own link in the doc, once the server sends its id (A11); null until then. */
function commentHref(url: string, commentId: string | null | undefined): string | null {
    if (!url || !commentId) return null;
    return `${url}${url.includes("?") ? "&" : "?"}disco=${encodeURIComponent(commentId)}`;
}

/**
 * One shared doc in the merchant drawer. The status line says where it stands
 * and the date that matters; the body what it asks of them now; the foot opens
 * the document, where every reply is made.
 */
export function DocSheet({
    doc,
    payload,
    flow,
    refreshFailed = false,
    onRetry,
    drafts,
    setDraft,
}: {
    doc: MerchantDocument;
    payload: ClientPagePayload;
    flow: SheetFlow;
    /** The last read again failed: what shows is the last good read. */
    refreshFailed?: boolean;
    onRetry?: () => void;
    /** The host's unsent answers (keyed by docAnswerKey), so moving on loses nothing. Phase 2 only. */
    drafts?: Record<string, AnswerDraft>;
    setDraft?: (key: string, draft: AnswerDraft | null) => void;
}) {
    const [opened, setOpened] = useState(false);
    const type = fileTypeOf(doc);
    const title = docTitle(doc);
    const status = MERCHANT_DOC_STATUS[doc.state] ?? MERCHANT_DOC_STATUS.nothing_asked;
    const who = accountManager(payload);
    const needs = doc.state === "needs_your_reply";
    const shared = docDate(doc.sharedAt);
    const updated = docDate(doc.updatedAt);
    const due = docDue(doc);
    const questions = doc.openQuestions.filter((q) => questionText(q.text));
    const waiting = docWaitingCount(doc);
    const answered = doc.questions?.approved ?? 0;
    const purpose = purposeList(docPurpose(doc, payload));
    // Phase 2: answered here, one question at a time, and posted into the doc for them.
    const answerHere = DOCS_WRITE_ON && needs && questions.length > 0 && Boolean(doc.url) && drafts !== undefined && setDraft !== undefined;

    let clock: DrawerStatus["clock"] = null;
    if (needs && due) clock = { text: due.text, late: due.late };
    else if ((doc.state === "with_us" || doc.state === "approved") && (updated || shared)) clock = { text: updated ? `Updated ${updated}` : `Shared ${shared}` };
    else if (shared) clock = { text: `Shared ${shared}` };

    // After Open, in the queue: the next item takes focus, so Enter moves on.
    useEffect(() => {
        if (opened) document.querySelector<HTMLElement>("[data-doc-next]")?.focus();
    }, [opened]);

    const openLink = (primary: boolean) => (
        <PillLink
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            variant={primary ? "primary" : "secondary"}
            icon={ExternalLinkIcon}
            aria-label={openName(doc)}
            onClick={() => {
                if (flow.inQueue) setOpened(true);
                // The client's own open, never a staffer's preview (#6108).
                if (!payload.previewedByStaff && !readViewAs().active) void markDocumentRead(doc.id);
            }}
        >
            {MERCHANT_OPEN_LABEL}
        </PillLink>
    );

    let foot: ReactNode;
    if (!doc.url) {
        foot = <DrawerFoot message={{ tone: "info", text: "It's in your email." }} />;
    } else if (opened && flow.inQueue) {
        foot = (
            <DrawerFoot
                message={{ tone: "info", text: "Opened in a new tab. Your replies show here within a few minutes." }}
                first={
                    flow.next ? (
                        <PillButton variant="primary" data-doc-next="" aria-label={`Next: ${flow.next.title}`} onClick={flow.goNext}>
                            Next: {cut(flow.next.title, 40)}
                        </PillButton>
                    ) : (
                        <PillButton variant="primary" data-doc-next="" onClick={flow.finish}>
                            Done
                        </PillButton>
                    )
                }
                second={openLink(false)}
            />
        );
    } else {
        foot = <DrawerFoot first={openLink(needs)} />;
    }

    return (
        <>
            <DrawerHead
                lead={<IconSquare tone={type.tone} icon={type.icon} />}
                leadKind="square"
                title={title}
                clampTitle
                status={{ tone: status.tone, label: status.label, clock }}
            />
            {refreshFailed && (
                <DrawerNotice
                    action={
                        onRetry && (
                            <PillButton variant="text" onClick={onRetry}>
                                Try again
                            </PillButton>
                        )
                    }
                >
                    Could not check this document just now. This is the last read.
                </DrawerNotice>
            )}
            {answerHere && drafts && setDraft && (
                <DocAnswer doc={doc} who={who} flow={flow} drafts={drafts} setDraft={setDraft} openLink={openLink} />
            )}
            {needs && !answerHere && (
                <DrawerBlock label={waiting > 0 ? `Reply in the document · ${waiting}` : "Reply in the document"}>
                    <p className={cn(TYPE.tc, "m-0 text-gray-500")}>Reply as a comment on each question.</p>
                    {questions.length > 0 && (
                        <ol className="list-plain m-0 mt-2 p-0">
                            {questions.map((q, i) => {
                                const asked = askedText(q.askedAt, doc.sharedAt);
                                const href = commentHref(doc.url, (q as { driveCommentId?: string | null }).driveCommentId);
                                return (
                                    <li key={q.id} className={cn("flex gap-3 border-t py-2.5 first:border-t-0 first:pt-0 last:pb-0", SURFACE.hairline)}>
                                        <span aria-hidden className={cn(TYPE.tc, "w-4 shrink-0 pt-px tabular-nums text-gray-400")}>
                                            {i + 1}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <p className="m-0 whitespace-pre-line break-words">{questionText(q.text)}</p>
                                            {(asked || href) && (
                                                <p className={cn(TYPE.tm, "m-0 mt-0.5 flex flex-wrap items-center gap-x-3 tabular-nums text-gray-500")}>
                                                    {asked}
                                                    {href && (
                                                        <a
                                                            href={href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            aria-label={`Reply to question ${i + 1} in the document, new tab`}
                                                            className={cn("inline-flex items-center gap-1 rounded font-medium text-blue-600 hover:underline", FOCUS_RING)}
                                                        >
                                                            Reply
                                                            <ExternalLinkIcon aria-hidden size={12} />
                                                        </a>
                                                    )}
                                                </p>
                                            )}
                                        </div>
                                    </li>
                                );
                            })}
                        </ol>
                    )}
                    {answered > 0 && <p className={cn(TYPE.tc, "m-0 mt-2 text-gray-500")}>{answered} already answered</p>}
                </DrawerBlock>
            )}
            {doc.state === "with_us" && (
                <DrawerBlock>
                    <p className="m-0">{who} replies next, in the document.</p>
                </DrawerBlock>
            )}
            <AnsweredFold answered={doc.answered} who={who} />
            {purpose && (
                <DrawerBlock label="What it's for">
                    {/* One name to a line, wrapped in full: a phone has no hover to show a cut one. */}
                    <ul className="list-plain m-0 space-y-0.5 p-0">
                        {purpose.shown.map((name) => (
                            <li key={name} className="break-words">
                                {name}
                            </li>
                        ))}
                    </ul>
                    {purpose.more > 0 && <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>and {purpose.more} more</p>}
                </DrawerBlock>
            )}
            {answerHere ? null : flow.done ? <DrawerFoot done={flow.done} /> : foot}
        </>
    );
}

/** Cut to `n` characters with an ellipsis; the whole title is the button's accessible name. */
function cut(text: string, n: number): string {
    return text.length > n ? `${text.slice(0, n - 1).trimEnd()}…` : text;
}
